/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabriele
 * File: ImageDataTab.tsx
 * Project: mle-client-n
 * Committed last: 2022/2/12 @ 1916
 * ---
 * Description:
 */


import { observer } from 'mobx-react';
import {
  Editor
}                   from '../../../../store/observables/Editor';
import {
  ObservableImage
}                   from '../../../../store/observables/ObservableImage';
import {
  Button,
  Divider,
  Intent
}                   from '@blueprintjs/core';
import {
  currentThemeIsDark,
  dataURLFromBase64,
  getAssetNameByBucketUrl
}                   from '../../../../common/utils';
import {
  useCallback,
  useState
}                   from 'react';
import {
  CropImageDialog
}                   from '../../dialogs/CropImageDialog';
import {
  useSelector
}                   from 'react-redux';
import {
  RootState
}                   from '../../../../store';
import classNames   from 'classnames';
import {
  mleDomainGqlClient
}                   from '../../../../api/domain';
import {
  IMAGE_BY_ID
}                   from '../../../../api/domain/queries.gql';

interface Props {
  editor: Editor;
}

export const ImageDataTab = observer((props: Props) => {
  const companyName = useSelector((state: RootState) => state.authState.user?.company?.name);
  const [cropImageOpen, setCropImageOpen] = useState(false);

  const handleRestoreOriginal = useCallback(() => {
    props.editor.getSelectedShape().setSize(props.editor.getSelectedShape<ObservableImage>().originalSize,
      { applyToPamelaShape: true });
    props.editor.getSelectedShape<ObservableImage>().resetCrop({ applyToPamelaShape: true });
  }, []);

  const downloadImage = useCallback(async () => {
    const { data } = await mleDomainGqlClient.query({
      query: IMAGE_BY_ID,
      variables: {
        id: props.editor.getSelectedShape<ObservableImage>().assetId
      }
    });

    const uri = dataURLFromBase64(data.image.content);
    const link = document.createElement('a');
    link.download = getAssetNameByBucketUrl(props.editor.getSelectedShape<ObservableImage>().src);
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    link.remove();
  }, []);

  return (
    <div>
      <div className="bp4-text-muted mle-info-heading">
        Origine
      </div>
      <Button
        icon="reset"
        small
        onClick={ handleRestoreOriginal }
      >
        Ripristina originale
      </Button>
      <div className="bp4-text-muted mt-2">
        L&apos;immagine è stata importata dalla libreria
        di <strong>{ companyName }</strong> con dimensioni originali di {
        `${ props.editor.pixelToCurrent(props.editor.getSelectedShape<ObservableImage>().originalSize.getWidth()) }x${ props.editor.pixelToCurrent(
          props.editor.getSelectedShape<ObservableImage>().originalSize.getHeight()) }${ props.editor.projectData.projectOptions.measureUnit }`
      } <br/><br/>
        File caricato: <span
        className={ classNames({
          'text-white': currentThemeIsDark(),
          'text-black': !currentThemeIsDark()
        }) }>{ `${ getAssetNameByBucketUrl(
        props.editor.getSelectedShape<ObservableImage>().src) }` }
        <Button
          icon="cloud-download"
          className="ml-1"
          small
          minimal
          intent={ Intent.PRIMARY }
          onClick={ downloadImage }
        />
      </span>
      </div>
      <Divider className="my-2"/>
      <div className="my-2">
        <div className="bp4-text-muted mle-info-heading">
          Strumenti
        </div>
        <Button
          icon="cut"
          small
          intent={ Intent.SUCCESS }
          onClick={ () => setCropImageOpen(true) }
        >
          Ritaglia
        </Button>
      </div>
      {
        cropImageOpen &&
        <CropImageDialog
          editor={ props.editor } isOpen={ cropImageOpen }
          onClose={ () => setCropImageOpen(false) }
          asset={ props.editor.getSelectedShape<ObservableImage>() }
          assetId={ props.editor.getSelectedShape<ObservableImage>().assetId }
        />
      }
    </div>
  );
});
